// Informational content shown in the footer InfoModal (demo copy)
export const infoContent = {
  'Order Book Depth': {
    title: 'Order Book Depth',
    tag: 'MARKETS',
    body: [
      { p: 'Every unlisted market on ApexForge is powered by a live two-sided order book. Bids and asks are aggregated from accredited buyers and verified shareholders, giving you real-time visibility into available liquidity at each price level.' },
      { h: 'What you see', p: 'Best bid / best ask, cumulative size at each level, and the current spread. Depth bars visualise where liquidity is concentrated so you can size your order intelligently.' },
      { h: 'How to use it', p: 'Open any company card and tap "View Order Book" to inspect depth before placing a buy order through your funded settlement vault.' },
    ],
  },
  'Valuation Data': {
    title: 'Valuation Data',
    tag: 'MARKETS',
    body: [
      { p: 'Implied valuations are derived from the last traded secondary share price multiplied by each company\u2019s fully-diluted share count, then reconciled against the most recent primary funding round.' },
      { h: 'Methodology', p: 'We blend last-trade pricing, 24h volume-weighted averages and primary round marks to produce a single implied enterprise valuation displayed on every market card.' },
      { h: 'Disclaimer', p: 'All valuations shown are simulated for demonstration and should not be relied upon as investment advice or a fair-value assessment.' },
    ],
  },
  'Vault Funding': {
    title: 'Vault Funding',
    tag: 'PLATFORM',
    body: [
      { p: 'Your settlement vault is a dedicated escrow balance used to settle secondary share purchases. Funds must clear in your vault before an order can be filled.' },
      { h: 'Supported rails', p: 'Crypto deposits in USDT and USDC (ERC20 or BEP20) and BTC. Each deposit generates a unique address and QR code; once you submit your transaction hash, our treasury desk verifies the on-chain payment and credits your vault.' },
      { h: 'Settlement', p: 'Verified deposits are credited within minutes to hours. You are notified by email the moment your deposit is confirmed or if it needs attention.' },
    ],
  },
  'Architecture & Spec': {
    title: 'Architecture & Specification',
    tag: 'PLATFORM',
    body: [
      { p: 'ApexForge operates as an institutional Alternative Trading System (ATS) for private US company shares, matching accredited buyers with verified shareholders.' },
      { h: 'Core components', p: 'Identity & accreditation (KYC), a per-market central limit order book, escrow-based settlement vaults, and an administrative clearing desk for KYC and deposit verification.' },
      { h: 'Settlement model', p: 'Trades settle against pre-funded vault balances, eliminating counterparty funding risk. Share ownership is recorded to each member\u2019s portfolio on execution.' },
    ],
  },
  'API Access': {
    title: 'API Access',
    tag: 'PLATFORM',
    body: [
      { p: 'Institutional members can integrate directly with ApexForge to stream market data and manage orders programmatically.' },
      { h: 'Available endpoints', p: 'Market and pipeline listings, live order-book depth, portfolio holdings and vault balances, plus order placement for KYC-cleared institutional accounts.' },
      { h: 'Getting access', p: 'API credentials are issued to verified institutional desks on request. Contact our team at help@apexforge.io to begin onboarding.' },
    ],
  },
  'SEC Rule 506(c)': {
    title: 'SEC Rule 506(c)',
    tag: 'COMPLIANCE',
    body: [
      { p: 'ApexForge markets are offered under SEC Rule 506(c) of Regulation D, which permits general solicitation provided that all investors are verified accredited investors.' },
      { h: 'What it means for you', p: 'Before you can transact, we are required to verify your accredited status through our KYC & Accreditation Desk \u2014 including identity documents and proof of income, net worth or professional qualification.' },
      { h: 'Verification', p: 'Verification is performed once and unlocks trading across all unlisted markets on the platform.' },
    ],
  },
  'Accredited KYC': {
    title: 'Accredited Investor KYC',
    tag: 'COMPLIANCE',
    body: [
      { p: 'Our three-step Institutional KYC process collects your legal identity, a government-issued ID document and evidence of accredited-investor status.' },
      { h: 'Qualifying frameworks', p: 'Net worth over $1M (excluding primary residence), annual income over $200K ($300K joint), a FINRA Series 7/65/82 license, or an institution/entity with $5M+ in assets.' },
      { h: 'Review time', p: 'Submissions are reviewed by our compliance desk, typically within 24 hours. You are notified by email whether your accreditation is approved or requires re-submission.' },
    ],
  },
  'ROFR & SPV Rules': {
    title: 'ROFR & SPV Rules',
    tag: 'COMPLIANCE',
    body: [
      { p: 'Many private companies retain a Right of First Refusal (ROFR) on secondary transfers, and some transfers are effected through Special Purpose Vehicles (SPVs).' },
      { h: 'Right of First Refusal', p: 'Where a ROFR applies, the issuing company (or its designees) may elect to purchase the shares on the same terms before the transfer to a buyer is completed.' },
      { h: 'SPV structures', p: 'Certain positions are held via SPVs that provide economic exposure to the underlying shares. SPV terms, fees and carry are disclosed on the relevant market prior to purchase.' },
    ],
  },
  'Risk Disclosures': {
    title: 'Risk Disclosures',
    tag: 'COMPLIANCE',
    body: [
      { p: 'Investing in private, unlisted securities involves a high degree of risk and is suitable only for accredited investors who can bear a total loss of capital.' },
      { h: 'Key risks', p: 'Illiquidity and long holding periods, limited public information, valuation uncertainty, dilution, and the possibility that a company never lists or is acquired below your entry price.' },
      { h: 'No advice', p: 'Nothing on ApexForge constitutes investment, legal or tax advice. This platform is a demonstration; all data is simulated and it is not an offer to buy or sell securities.' },
    ],
  },
  'About ApexForge': {
    title: 'About ApexForge',
    tag: 'COMPANY',
    body: [
      { p: 'ApexForge is an institutional Alternative Trading System bringing transparent, members-only secondary liquidity to shares of the world\u2019s leading private companies.' },
      { h: 'Our mission', p: 'To give accredited investors and institutions fair, orderly access to pre-IPO unicorn equity \u2014 with live order books, escrow settlement and rigorous compliance at the core.' },
      { h: 'Group structure', p: 'ApexForge operates through regulated entities in the Republic of Seychelles, South Africa (FSCA) and Cyprus. Full registered addresses are available in the Contact section.' },
    ],
  },
  'Institutional Desk': {
    title: 'Institutional Desk',
    tag: 'COMPANY',
    body: [
      { p: 'Our Institutional Desk provides high-touch coverage for funds, family offices and corporate treasuries executing large private-market allocations.' },
      { h: 'Services', p: 'Block liquidity sourcing, indicative pricing on pipeline names, SPV structuring, and dedicated settlement support with priority deposit verification.' },
      { h: 'Get in touch', p: 'Reach the desk directly at help@apexforge.io to discuss mandates and onboarding.' },
    ],
  },
  'Careers': {
    title: 'Careers',
    tag: 'COMPANY',
    body: [
      { p: 'We\u2019re building the infrastructure for private-market liquidity and are always looking for exceptional people across engineering, compliance, trading operations and institutional sales.' },
      { h: 'How we work', p: 'Small, high-ownership teams; a bias for shipping; and a deep respect for the regulatory responsibility that comes with handling investor capital.' },
      { h: 'Apply', p: 'Send your CV and a short note to help@apexforge.io with the role you\u2019re interested in.' },
    ],
  },
};
